import React, { useState } from 'react';
import { ArrowLeft, Lightbulb, Plus, Trash2 } from 'lucide-react';

interface Props {
  onBack: () => void;
  onSave?: () => void;
}

export const ForeignQuotaNotificationSettingsView: React.FC<Props> = ({ onBack, onSave }) => {
  const [thresholds, setThresholds] = useState<number[]>([40, 45]);

  const handleAddThreshold = () => {
    setThresholds((prev) => [...prev, 48]);
  };

  const handleRemoveThreshold = (index: number) => {
    setThresholds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleThresholdChange = (index: number, val: number) => {
    setThresholds((prev) => {
      const next = [...prev];
      next[index] = val;
      return next;
    });
  };

  const handleSave = () => {
    if (onSave) onSave();
  };

  return (
    <div className="bg-white min-h-[480px] rounded-xl p-6 sm:p-8 font-prompt space-y-6 flex flex-col justify-between">
      <div className="space-y-6">
        {/* Top Header */}
        <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
          <button
            type="button"
            onClick={onBack}
            className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors cursor-pointer"
            title="ย้อนกลับ"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
              ตั้งค่าการแจ้งเตือนกรรมสิทธิ์ชาวต่างชาติ
            </h1>
            <span className="text-amber-500 text-lg" title="คำแนะนำ">
              💡
            </span>
          </div>
        </div>

        {/* Description Banner */}
        <p className="text-xs sm:text-sm text-slate-700 leading-relaxed max-w-3xl">
          ระบบจะแจ้งเตือนไปยัง User เพื่อให้ทราบว่า ณ ปัจจุบันมีชาวต่างชาติถือครองกรรมสิทธิ์ภายในโครงการเป็นสัดส่วนกี่ % จากเจ้าของกรรมสิทธิ์ทั้งหมด เพื่อแจ้งเตือนล่วงหน้าก่อนเกินโควต้า
        </p>

        {/* Threshold Inputs */}
        <div className="space-y-5 pt-2 max-w-md">
          {thresholds.map((threshold, index) => (
            <div key={index} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs sm:text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <span>เกณฑ์การแจ้งเตือนลำดับที่ {index + 1}</span>
                  <span className="text-amber-500 text-xs">💡</span>
                </label>
                {thresholds.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveThreshold(index)}
                    className="text-rose-500 hover:text-rose-700 text-xs flex items-center gap-1 cursor-pointer"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> ลบ
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={49}
                  value={threshold || ''}
                  onChange={(e) => handleThresholdChange(index, Number(e.target.value))}
                  className="w-full sm:w-48 p-2.5 text-xs sm:text-sm border border-slate-300 rounded-md bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span className="text-sm font-semibold text-slate-700">%</span>
              </div>
            </div>
          ))}

          {/* Add Threshold Button */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleAddThreshold}
              className="px-4 py-2 border border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-700 rounded-md text-xs sm:text-sm font-semibold transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-4 h-4 text-slate-600" />
              <span>เพิ่มการแจ้งเตือน</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Save / Cancel Buttons */}
      <div className="flex justify-end items-center gap-3 pt-8 border-t border-slate-100">
        <button
          type="button"
          onClick={onBack}
          className="px-6 py-2.5 bg-slate-300 hover:bg-slate-400 text-slate-700 rounded-md text-sm font-semibold transition-colors cursor-pointer"
        >
          ยกเลิก
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="px-6 py-2.5 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-semibold transition-colors shadow-xs cursor-pointer"
        >
          บันทึก
        </button>
      </div>
    </div>
  );
};
